import React from 'react';
import Icon from '../../../components/AppIcon';

const ProjectStats = ({ projects }) => {
  const stats = [
    {
      icon: 'FolderOpen',
      label: 'Total Projects',
      value: projects.length,
      color: 'text-primary bg-primary/10 border-primary/20'
    },
    {
      icon: 'Globe',
      label: 'Live Projects',
      value: projects.filter(p => p.status === 'Live').length,
      color: 'text-success bg-success/10 border-success/20'
    },
    {
      icon: 'Code',
      label: 'Technologies Used',
      value: [...new Set(projects.flatMap(p => p.techStack))].length,
      color: 'text-accent bg-accent/10 border-accent/20'
    },
    {
      icon: 'Users',
      label: 'Total Users Served',
      value: projects.reduce((sum, p) => {
        const userMetric = p.metrics?.find(m => m.label.toLowerCase().includes('user'));
        if (userMetric) {
          const numStr = userMetric.value.replace(/[^\d]/g, '');
          return sum + parseInt(numStr || 0);
        }
        return sum;
      }, 0).toLocaleString() + '+',
      color: 'text-brand-purple bg-brand-purple/10 border-brand-purple/20'
    }
  ];

  const techStats = [...new Set(projects.flatMap(p => p.techStack))]
    .map(tech => ({
      name: tech,
      count: projects.filter(p => p.techStack.includes(tech)).length
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="BarChart3" size={20} className="text-accent" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Project Statistics</h3>
          <p className="text-sm text-text-secondary">Overview of my development portfolio</p>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className="text-center p-4 bg-surface rounded-lg">
            <div className={`w-12 h-12 mx-auto mb-3 rounded-lg flex items-center justify-center border ${stat.color}`}>
              <Icon name={stat.icon} size={24} />
            </div>
            <div className="text-2xl font-bold text-text-primary mb-1">{stat.value}</div>
            <div className="text-sm text-text-muted">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Technology Distribution */}
      <div>
        <h4 className="text-md font-semibold text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="Code" size={16} className="text-primary" />
          <span>Most Used Technologies</span>
        </h4>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {techStats.map((tech, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-surface rounded-lg">
              <span className="text-sm font-medium text-text-primary">{tech.name}</span>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-2 bg-border rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full transition-premium"
                    style={{ width: `${(tech.count / projects.length) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-text-muted">{tech.count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Project Timeline */}
      <div className="mt-8 pt-6 border-t border-border">
        <h4 className="text-md font-semibold text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-primary" />
          <span>Project Timeline</span>
        </h4>
        
        <div className="flex items-center space-x-4 overflow-x-auto pb-2">
          {[...new Set(projects.map(p => p.year))].sort().reverse().map((year, index) => {
            const yearProjects = projects.filter(p => p.year === year);
            return (
              <div key={index} className="flex-shrink-0 text-center p-3 bg-surface rounded-lg min-w-[100px]">
                <div className="text-lg font-bold text-primary">{year}</div>
                <div className="text-sm text-text-muted">
                  {yearProjects.length} project{yearProjects.length !== 1 ? 's' : ''}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProjectStats;